// 볼린저 밴드 배율 상태 관리 (DAS-159, DAS-162)
// 배율 m == 구동 전략 usdt_upbit_spot 의 safer.json Level 과 동일한 의미론:
//   m=1.0 → 백엔드 raw ±2σ 그대로 (safer Level 1.0), m=2.0 → 운영 Level 2.0 밴드.
// 변환 로직은 Strategy deltafx safer/tier.go AdjustBand(Default 티어)와 동일하다.
// 마지막 설정값은 localStorage에 영속화되어 다음 방문 시 그대로 복원된다.
// 같은 페이지에 여러 게이지 인스턴스가 떠 있으므로 CustomEvent로 실시간 동기화한다.

// 테스트에서 동일 키를 하드코딩하지 않도록 export
export const BAND_MULTIPLIER_STORAGE_KEY = "dashboard_bollingerBandMultiplier";
const STORAGE_KEY = BAND_MULTIPLIER_STORAGE_KEY;
const CHANGE_EVENT = "dashboard:bollinger-multiplier-change";

export const BAND_MULTIPLIER_MIN = 0.1;
export const BAND_MULTIPLIER_MAX = 5;
export const BAND_MULTIPLIER_STEP = 0.1;
export const BAND_MULTIPLIER_DEFAULT = 1;

export const clampBandMultiplier = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return BAND_MULTIPLIER_DEFAULT;
    const clamped = Math.min(BAND_MULTIPLIER_MAX, Math.max(BAND_MULTIPLIER_MIN, num));
    // 0.1 스텝 부동소수점 잔여 오차 제거 (예: 1.2000000000000002)
    return Math.round(clamped * 100) / 100;
};

export const getBandMultiplier = () => {
    try {
        return clampBandMultiplier(localStorage.getItem(STORAGE_KEY) ?? BAND_MULTIPLIER_DEFAULT);
    } catch {
        return BAND_MULTIPLIER_DEFAULT;
    }
};

export const setBandMultiplier = (value) => {
    const next = clampBandMultiplier(value);
    try {
        localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
        // localStorage 불가 환경(사파리 프라이빗 등)에서도 이벤트 동기화는 유지
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
    return next;
};

// 배율 변경 구독 — 같은 탭(CustomEvent) + 다른 탭(storage) 모두 반영. unsubscribe 함수 반환.
export const subscribeBandMultiplier = (callback) => {
    const onChange = (event) => callback(clampBandMultiplier(event.detail));
    const onStorage = (event) => {
        if (event.key === STORAGE_KEY) callback(getBandMultiplier());
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
        window.removeEventListener(CHANGE_EVENT, onChange);
        window.removeEventListener("storage", onStorage);
    };
};

// usdt_upbit_spot safer Default 티어 설정 (Strategy safer.json + tier.go threshold).
// HighCap "high": Level 확장 후 상단밴드를 raw high 로 캡 (Level은 low만 넓힘).
// MIN_BAND_WIDTH: 폭이 미달이면 high = low + 최소폭 (tier.go 하드코딩 threshold 8.0).
export const BAND_HIGH_CAP = "high"; // "high" | "middle" | "low" | "none"
export const MIN_BAND_WIDTH = 8.0;

// raw 밴드에 배율 적용 — Strategy safer/tier.go AdjustBand(Default 티어)와 동일 로직 (DAS-162):
//  1. low/high 를 middle 중심으로 Level(=multiplier)배 확장
//  2. HighCap: high 를 raw 밴드의 high/middle/low 값으로 캡 ("none"은 조정값 유지)
//  3. 최소폭: high−low < MIN_BAND_WIDTH 이면 high = low + MIN_BAND_WIDTH
// middle이 없으면 low/high 중점을 중심으로 사용. 중심을 못 구하면 원본 그대로 반환.
export const applyBandMultiplier = ({ low, middle, high }, multiplier) => {
    const m = clampBandMultiplier(multiplier);
    const lowNum = Number(low);
    const middleNum = Number(middle);
    const highNum = Number(high);

    let center = middleNum;
    if (!Number.isFinite(center) && Number.isFinite(lowNum) && Number.isFinite(highNum)) {
        center = (lowNum + highNum) / 2;
    }
    if (!Number.isFinite(center)) {
        return { low: lowNum, middle: middleNum, high: highNum };
    }

    // 1. Level 확장 (tier.go: adjusted = raw*L - middle*(L-1) = middle + (raw-middle)*L)
    let adjLow = Number.isFinite(lowNum) ? center - (center - lowNum) * m : lowNum;
    let adjHigh = Number.isFinite(highNum) ? center + (highNum - center) * m : highNum;

    // 2. HighCap — raw 밴드 값으로 상단 캡
    if (BAND_HIGH_CAP === "middle") {
        adjHigh = center;
    } else if (BAND_HIGH_CAP === "low") {
        adjHigh = lowNum;
    } else if (BAND_HIGH_CAP === "high") {
        adjHigh = highNum;
    }

    // 3. 최소폭 보정
    if (Number.isFinite(adjLow) && Number.isFinite(adjHigh) && adjHigh - adjLow < MIN_BAND_WIDTH) {
        adjHigh = adjLow + MIN_BAND_WIDTH;
    }

    return { low: adjLow, middle: middleNum, high: adjHigh };
};
